import React, { useState, useEffect } from "react";
import { get } from "../../services/api";
import jsPDF from "jspdf";
import "jspdf-autotable";

function BillingReport() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const [filterCenter, setFilterCenter] = useState(savedCenterId || "");
  const [filterClass, setFilterClass] = useState("");
  const [filterSection, setFilterSection] = useState("");
  
  const getFirstDayOfMonth = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  };
  const getToday = () => new Date().toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState(getFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getToday());
  
  const [centersData, setCentersData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);

  const [filteredData, setFilteredData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalRecords: 0,
    totalReceived: 0,
    totalDiscount: 0,
    grandTotal: 0
  });
  const [loading, setLoading] = useState(false);

  // Fetch Centers
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCentersData(response || []);
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };
    if (organizationId && token) fetchCenters();
  }, [organizationId, token]);

  // Fetch Classes
  useEffect(() => {
    const fetchClasses = async () => {
      if (!filterCenter) {
        setClassesData([]);
        setFilterClass("");
        return;
      }
      try {
        const response = await get(
          `CenterClass/GetClassCenterByCenterId?id=${filterCenter}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClassesData(response || []);
      } catch (error) {
        console.error("Error fetching classes:", error);
      }
    };
    fetchClasses();
  }, [filterCenter, token]);

  // Fetch Sections
  useEffect(() => {
    const fetchSections = async () => {
      if (!filterClass || !filterCenter) {
        setSectionsData([]);
        setFilterSection("");
        return;
      }
      try {
        const response = await get(
          `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${filterClass}&centerId=${filterCenter}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSectionsData(response || []);
      } catch (error) {
        console.error("Error fetching sections:", error);
      }
    };
    fetchSections();
  }, [filterClass, filterCenter, token]);

  const applyFilter = async () => {
    setLoading(true);
    try {
      const CenterId = filterCenter || 0;
      const ClassId = filterClass || 0;
      const SectionId = filterSection || 0;
      
      const response = await get(
        `StudentChallan/GetDailyFeeCollectionReport?CenterId=${CenterId}&ClassId=${ClassId}&SectionId=${SectionId}&FromDate=${dateFrom}&ToDate=${dateTo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response && response.isSuccess) {
        setFilteredData(response.result?.details || []);
        setSummaryData(response.result?.summary || {
          totalRecords: 0,
          totalReceived: 0,
          totalDiscount: 0,
          grandTotal: 0
        });
      } else {
        setFilteredData([]);
        setSummaryData({
          totalRecords: 0,
          totalReceived: 0,
          totalDiscount: 0,
          grandTotal: 0
        });
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setFilteredData([]);
      setSummaryData({
        totalRecords: 0,
        totalReceived: 0,
        totalDiscount: 0,
        grandTotal: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const resetFilter = () => {
    setFilterCenter(savedCenterId || "");
    setFilterClass("");
    setFilterSection("");
    setDateFrom(getFirstDayOfMonth());
    setDateTo(getToday());
    setFilteredData([]);
    setSummaryData({
      totalRecords: 0,
      totalReceived: 0,
      totalDiscount: 0,
      grandTotal: 0
    });
  };

  useEffect(() => {
    applyFilter();
  }, []);

  const formatNumber = (num) => (num || 0).toLocaleString('en-PK');
  const formatDateStr = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr.includes("T")) {
      const [y, m, d] = dateStr.split("T")[0].split("-");
      return `${d}-${m}-${y}`;
    }
    return dateStr;
  };

  const exportToCSV = () => {
    if (filteredData.length === 0) { alert("No data to export!"); return; }
    const headers = ["Date", "Class", "Section", "Received Amount", "Discount", "Total"];
    const rows = filteredData.map(item => [
      formatDateStr(item.date), item.class, item.section,
      item.receivedAmount, item.discount, item.total,
    ]);
    const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", `Fee_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    if (filteredData.length === 0) {
      alert("No data to export!");
      return;
    }
    const doc = new jsPDF("landscape");
    doc.text("Billing Report", 14, 15);
    doc.setFontSize(10);
    doc.text(`Date Range: ${formatDateStr(dateFrom)} – ${formatDateStr(dateTo)}`, 14, 22);

    const tableColumns = ["Date", "Class", "Section", "Received Amount", "Discount", "Total"];
    const tableRows = [];

    filteredData.forEach((item) => {
      const rowData = [
        formatDateStr(item.date),
        item.class,
        item.section,
        formatNumber(item.receivedAmount),
        formatNumber(item.discount),
        formatNumber(item.total)
      ];
      tableRows.push(rowData);
    });

    // Add Grand Total
    tableRows.push([
      `Grand Total (${summaryData.totalRecords} records)`, "", "",
      formatNumber(summaryData.totalReceived),
      formatNumber(summaryData.totalDiscount),
      formatNumber(summaryData.grandTotal)
    ]);

    doc.autoTable({
      head: [tableColumns],
      body: tableRows,
      startY: 28,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [59, 130, 246] },
      columnStyles: {
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' }
      },
      didParseCell: function (data) {
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [17, 24, 39];
          if (data.column.index === 0) data.cell.colSpan = 3;
        }
      }
    });

    doc.save(`Fee_Report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // ─── Shared styles ──────────────────────────────────────────────
  const inputStyle = {
    height: "36px",
    padding: "0 12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "13px",
    minWidth: "150px",
    outline: "none",
    color: "#111827",
    backgroundColor: "#ffffff",
  };

  const labelStyle = {
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#6b7280",
    marginBottom: "4px",
    display: "block",
  };

  const btnPrimary = {
    height: "36px",
    padding: "0 18px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#3b82f6",
    color: "#fff",
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
  };

  const btnSecondary = {
    height: "36px",
    padding: "0 18px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    color: "#374151",
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
  };

  const summaryCards = [
    { label: "Total Records",   value: summaryData.totalRecords,              color: "#3b82f6", bg: "#eff6ff" },
    { label: "Total Received",  value: formatNumber(summaryData.totalReceived), color: "#10b981", bg: "#f0fdf4" },
    { label: "Total Discount",  value: formatNumber(summaryData.totalDiscount), color: "#f59e0b", bg: "#fffbeb" },
    { label: "Grand Total",     value: formatNumber(summaryData.grandTotal),    color: "#ef4444", bg: "#fef2f2" },
  ];

  return (
    <div style={{ paddingBottom: "20px", fontFamily: "Inter, Arial, sans-serif" }}>
      {/* ── Page Header ── */}
      <div className="no-print" style={{
        backgroundColor: "#ffffff",
        borderBottom: "1px solid #e5e7eb",
        padding: "20px 28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#111827" }}>
            Fee Receive Report
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#6b7280" }}>
            Daily fee collection summary by class &amp; section
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={exportToCSV} style={{ ...btnSecondary, display: "flex", alignItems: "center", gap: "6px" }}>
            📥 Export CSV
          </button>
          <button onClick={exportToPDF} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: "6px" }}>
            📄 Export PDF
          </button>
        </div>
      </div>

      <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "20px" }}>

        {/* ── Filter Panel ── */}
        <div className="no-print" style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "20px 24px",
        }}>
          <p style={{ margin: "0 0 14px", fontSize: "13px", fontWeight: 600, color: "#374151" }}>Filters</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-end" }}>
            
            {/* Center */}
            <div className="d-none">
              <label style={labelStyle}>Center</label>
              <select value={filterCenter}  onChange={e => {
                setFilterCenter(e.target.value);
                setFilterClass("");
                setFilterSection("");
              }} style={inputStyle}>
                <option value="">All Centers</option>
                {centersData.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Class */}
            <div>
              <label style={labelStyle}>Class</label>
              <select 
                value={filterClass} 
                onChange={e => {
                  setFilterClass(e.target.value);
                  setFilterSection("");
                }} 
                style={{ ...inputStyle, backgroundColor: !filterCenter ? "#f9fafb" : "#ffffff", color: !filterCenter ? "#9ca3af" : "#111827" }}
                disabled={!filterCenter}
              >
                <option value="">All Classes</option>
                {classesData.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </select>
            </div>

            {/* Section */}
            <div>
              <label style={labelStyle}>Section</label>
              <select
                value={filterSection}
                onChange={e => setFilterSection(e.target.value)}
                disabled={!filterClass}
                style={{ ...inputStyle, backgroundColor: !filterClass ? "#f9fafb" : "#ffffff", color: !filterClass ? "#9ca3af" : "#111827" }}
              >
                <option value="">All Sections</option>
                {sectionsData.map(sec => (
                  <option key={sec.id} value={sec.id}>{sec.name}</option>
                ))}
              </select>
            </div>

            {/* Date From */}
            <div>
              <label style={labelStyle}>Date From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
            </div>

            {/* Date To */}
            <div>
              <label style={labelStyle}>Date To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
              <button onClick={applyFilter} style={btnPrimary}>Apply</button>
              <button onClick={resetFilter} style={btnSecondary}>Reset</button>
            </div>
          </div>
        </div>

        {/* ── Summary Cards ── */}
        <div className="no-print" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px" }}>
          {summaryCards.map((card, i) => (
            <div key={i} style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "16px 20px",
              display: "flex",
              alignItems: "center",
              gap: "14px",
            }}>
               {loading ? (
                <div style={{ padding: "10px", color: "#6b7280", fontSize: "14px" }}>Loading...</div>
              ) : (
                <>
                  <div style={{
                    width: "40px", height: "40px", borderRadius: "8px",
                    backgroundColor: card.bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: card.color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", color: "#6b7280" }}>
                      {card.label}
                    </div>
                    <div style={{ fontSize: "20px", fontWeight: 700, color: "#111827", marginTop: "2px" }}>
                      {card.value}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* ── Table ── */}
        <div style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          overflow: "hidden",
        }}>
          {/* Table header row */}
          <div style={{
            padding: "14px 20px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#f9fafb",
          }}>
            <span style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>Daily Fee Collection</span>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>
              {summaryData.totalRecords} record{summaryData.totalRecords !== 1 ? 's' : ''}
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "750px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  {["Date", "Class", "Section", "Received Amount", "Discount", "Total"].map((h, i) => (
                    <th key={i} style={{
                      padding: "11px 18px",
                      textAlign: i < 3 ? "left" : "right",
                      fontSize: "11px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "#6b7280",
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center", padding: "48px", color: "#6b7280" }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div style={{ marginTop: "10px" }}>Fetching data...</div>
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center", padding: "48px", color: "#6b7280" }}>
                      <div style={{ fontSize: "28px", marginBottom: "8px" }}>🔍</div>
                      No records found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, idx) => (
                    <tr key={idx} style={{
                      borderBottom: "1px solid #f3f4f6",
                      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fafafa",
                      transition: "background 0.15s",
                    }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = "#eff6ff"}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "#ffffff" : "#fafafa"}
                    >
                      <td style={{ padding: "12px 18px", fontSize: "13px", color: "#3b82f6", fontWeight: 500 }}>
                        {formatDateStr(item.date)}
                      </td>
                      <td style={{ padding: "12px 18px", fontSize: "13px", color: "#111827" }}>{item.class}</td>
                      <td style={{ padding: "12px 18px", fontSize: "13px", color: "#374151" }}>{item.section}</td>
                      <td style={{ padding: "12px 18px", fontSize: "13px", textAlign: "right", color: item.receivedAmount === 0 ? "#9ca3af" : "#111827" }}>
                        {formatNumber(item.receivedAmount)}
                      </td>
                      <td style={{ padding: "12px 18px", fontSize: "13px", textAlign: "right", color: item.discount === 0 ? "#9ca3af" : "#f59e0b", fontWeight: item.discount > 0 ? 500 : 400 }}>
                        {formatNumber(item.discount)}
                      </td>
                      <td style={{ padding: "12px 18px", fontSize: "13px", textAlign: "right", fontWeight: 600, color: "#111827" }}>
                        {formatNumber(item.total)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {!loading && filteredData.length > 0 && (
                <tfoot>
                  <tr style={{ backgroundColor: "#f0f9ff", borderTop: "2px solid #e5e7eb" }}>
                    <td colSpan="3" style={{ padding: "13px 18px", fontWeight: 700, fontSize: "13px", color: "#111827" }}>
                      Grand Total ({summaryData.totalRecords} records)
                    </td>
                    <td style={{ padding: "13px 18px", textAlign: "right", fontWeight: 700, fontSize: "13px", color: "#111827" }}>
                      {formatNumber(summaryData.totalReceived)}
                    </td>
                    <td style={{ padding: "13px 18px", textAlign: "right", fontWeight: 700, fontSize: "13px", color: "#f59e0b" }}>
                      {formatNumber(summaryData.totalDiscount)}
                    </td>
                    <td style={{ padding: "13px 18px", textAlign: "right", fontWeight: 700, fontSize: "14px", color: "#111827" }}>
                      {formatNumber(summaryData.grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* ── Footer ── */}
        <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af", textAlign: "right" }}>
          Report generated on {new Date().toLocaleString()}
        </p>
      </div>

      <style>{`
        @media print {
          body { background: white; }
          button { display: none !important; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default BillingReport;